import { logEvent } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import { db } from "./firebaseConfig";
import { collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";

// Analytics event logger for GA4
export const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
  try {
    // FIX: Check if analytics is initialized before using it
    if (analytics) {
      logEvent(analytics, eventName, eventData || {});
    }
  } catch (error) {
    // Silently fail or warn instead of crashing
    console.warn("Error tracking event to GA4 (analytics might be disabled):", error);
  }
};

// Custom analytics event logger for Firestore
interface AnalyticsEvent {
  eventName: string;
  userId: string;
  familyId: string;
  eventData: Record<string, any>;
  timestamp: any;
}

export const logAnalyticsEvent = async (
  eventName: string,
  userId: string,
  familyId: string,
  eventData?: Record<string, any>
) => {
  try {
    const event: AnalyticsEvent = {
      eventName,
      userId,
      familyId,
      eventData: eventData || {},
      timestamp: serverTimestamp(),
    };

    // 1. Log to Firestore (This always works if DB is connected)
    await addDoc(collection(db, "analytics"), event);

    // 2. Also track to GA4 (Safe call now)
    trackEvent(eventName, {
      user_id: userId,
      family_id: familyId,
      ...eventData,
    });
  } catch (error) {
    console.error("Error logging analytics event:", error);
  }
};

// User Activity Tracking
export const trackLogin = (userId: string, familyId: string) => {
  logAnalyticsEvent("login", userId, familyId, {
    timestamp: Date.now(),
  });
};

export const trackLogout = (userId: string, familyId: string) => {
  logAnalyticsEvent("logout", userId, familyId, {
    timestamp: Date.now(),
  });
};

export const trackSessionStart = (userId: string, familyId: string) => {
  logAnalyticsEvent("session_start", userId, familyId, {
    timestamp: Date.now(),
  });
};

export const trackSessionEnd = (userId: string, familyId: string, sessionDuration: number) => {
  logAnalyticsEvent("session_end", userId, familyId, {
    duration_seconds: sessionDuration,
    timestamp: Date.now(),
  });
};

// Feature Usage Tracking
export const trackPageView = (userId: string, familyId: string, pageName: string) => {
  logAnalyticsEvent("page_view", userId, familyId, {
    page_name: pageName,
    timestamp: Date.now(),
  });
};

export const trackFeatureUsage = (userId: string, familyId: string, featureName: string) => {
  logAnalyticsEvent("feature_used", userId, familyId, {
    feature_name: featureName,
    timestamp: Date.now(),
  });
};

// Content Engagement Tracking
export const trackPostCreated = (userId: string, familyId: string, postData: any) => {
  logAnalyticsEvent("post_created", userId, familyId, {
    content_type: postData.type || "post",
    has_media: !!postData.media,
    timestamp: Date.now(),
  });
};

export const trackPostLiked = (userId: string, familyId: string, postId: string, authorId: string) => {
  logAnalyticsEvent("post_liked", userId, familyId, {
    post_id: postId,
    author_id: authorId,
    timestamp: Date.now(),
  });
};

export const trackPostCommented = (userId: string, familyId: string, postId: string, commentData: any) => {
  logAnalyticsEvent("post_commented", userId, familyId, {
    post_id: postId,
    comment_length: commentData.content?.length || 0,
    timestamp: Date.now(),
  });
};

export const trackMessageSent = (userId: string, familyId: string, conversationId: string, recipientId: string) => {
  logAnalyticsEvent("message_sent", userId, familyId, {
    conversation_id: conversationId,
    recipient_id: recipientId,
    timestamp: Date.now(),
  });
};

export const trackSearch = (userId: string, familyId: string, searchQuery: string, resultCount: number) => {
  logAnalyticsEvent("search_performed", userId, familyId, {
    query: searchQuery,
    result_count: resultCount,
    timestamp: Date.now(),
  });
};

export const trackSearchResult = (userId: string, familyId: string, resultType: string, resultId: string) => {
  logAnalyticsEvent("search_result_clicked", userId, familyId, {
    result_type: resultType,
    result_id: resultId,
    timestamp: Date.now(),
  });
};

// Member Management Tracking
export const trackMemberAdded = (userId: string, familyId: string, memberData: any) => {
  logAnalyticsEvent("member_added", userId, familyId, {
    relationship: memberData.relationship,
    timestamp: Date.now(),
  });
};

export const trackInviteSent = (userId: string, familyId: string, email: string) => {
  logAnalyticsEvent("invite_sent", userId, familyId, {
    invited_email: email,
    timestamp: Date.now(),
  });
};

export const trackInviteAccepted = (userId: string, familyId: string) => {
  logAnalyticsEvent("invite_accepted", userId, familyId, {
    timestamp: Date.now(),
  });
};

// Admin Actions Tracking
export const trackAdminAction = (userId: string, familyId: string, actionType: string, actionDetails: any) => {
  logAnalyticsEvent("admin_action", userId, familyId, {
    action_type: actionType,
    ...actionDetails,
    timestamp: Date.now(),
  });
};

// Notification Tracking
export const trackNotificationOpened = (userId: string, familyId: string, notificationType: string) => {
  logAnalyticsEvent("notification_opened", userId, familyId, {
    notification_type: notificationType,
    timestamp: Date.now(),
  });
};

export const trackNotificationDismissed = (userId: string, familyId: string, notificationType: string) => {
  logAnalyticsEvent("notification_dismissed", userId, familyId, {
    notification_type: notificationType,
    timestamp: Date.now(),
  });
};

// Preference Changes
export const trackPreferenceChanged = (userId: string, familyId: string, preferenceName: string, newValue: any) => {
  logAnalyticsEvent("preference_changed", userId, familyId, {
    preference_name: preferenceName,
    new_value: newValue,
    timestamp: Date.now(),
  });
};

// Error Tracking
export const trackError = (userId: string, familyId: string, errorMessage: string, errorStack?: string) => {
  logAnalyticsEvent("error_occurred", userId, familyId, {
    error_message: errorMessage,
    error_stack: errorStack,
    timestamp: Date.now(),
  });
};

// Performance Tracking
export const trackPageLoadTime = (userId: string, familyId: string, pageName: string, loadTime: number) => {
  logAnalyticsEvent("page_load_time", userId, familyId, {
    page_name: pageName,
    load_time_ms: loadTime,
    timestamp: Date.now(),
  });
};

export const trackAPILatency = (userId: string, familyId: string, apiName: string, latency: number) => {
  logAnalyticsEvent("api_latency", userId, familyId, {
    api_name: apiName,
    latency_ms: latency,
    timestamp: Date.now(),
  });
};

// Get analytics data for dashboard
export const getAnalyticsData = async (
  familyId: string,
  startDate: Date,
  endDate: Date
) => {
  try {
    const q = query(
      collection(db, "analytics"),
      where("familyId", "==", familyId),
      where("timestamp", ">=", startDate),
      where("timestamp", "<=", endDate)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching analytics data:", error);
    return [];
  }
};

// Get event count by type
export const getEventCountByType = async (
  familyId: string,
  eventName: string,
  startDate: Date,
  endDate: Date
) => {
  try {
    const q = query(
      collection(db, "analytics"),
      where("familyId", "==", familyId),
      where("eventName", "==", eventName),
      where("timestamp", ">=", startDate),
      where("timestamp", "<=", endDate)
    );

    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("Error fetching event count:", error);
    return 0;
  }
};

// Get unique users by event
export const getUniqueUsersByEvent = async (
  familyId: string,
  eventName: string,
  startDate: Date,
  endDate: Date
) => {
  try {
    const q = query(
      collection(db, "analytics"),
      where("familyId", "==", familyId),
      where("eventName", "==", eventName),
      where("timestamp", ">=", startDate),
      where("timestamp", "<=", endDate)
    );

    const snapshot = await getDocs(q);
    const userIds = new Set(snapshot.docs.map((doc) => doc.data().userId));
    return userIds.size;
  } catch (error) {
    console.error("Error fetching unique users:", error);
    return 0;
  }
};